import { connect } from 'react-redux';

import {
  getColumns,
  getExcludedRanges,
  getIsSelectedCellInSelectedRange,
  getIsSelectingRange,
  getKeys,
  getRows,
  getSelectedCell,
  getSelectedExcludedRanges,
  getSelectedRange,
  getTotalColumns,
  getTotalRows
} from '../_selectors';

import {
  cellsToRange,
  columnToChars,
  containedInRange,
  containedInRanges,
  isSameCell
} from '../_utils';

import { FC, useCallback, useEffect, useState } from 'react';
import { cn } from 'utils/cn';
import { selectCell, toggleSelectTableRow } from '../_actions';
import { getShouldPreventEditing } from '../_selectors';

type Cell = {
  row: number | string;
  column: number | string;
};

export type Range = { start: Cell; end: Cell; remove: boolean; originalIndex: number };

const inHoveringRange = (
  cell: Cell,
  selectedCell: Cell,
  lastHoveredCell: Cell | null,
  isSelectingRange: boolean
) => {
  if (!isSelectingRange) return false;
  if (!lastHoveredCell) return false;

  const range = cellsToRange(selectedCell, lastHoveredCell);

  return containedInRange(cell, range);
};

interface IBody {
  columns: (number | string)[];
  rows: (number | string)[];
  keys: Record<string, string>;
  totalColumns: number;
  totalRows: number;
  toggleSelectTableRow: (row: number | string) => void;
  selectedCell: Cell;
  selectCell: (cell: Cell) => void;
  shouldPreventEditing: boolean;
  selectedRange: Range;
  excludedRanges: Range[];
  selectedExcludedRanges: Range[];
  isSelectedCellInSelectedRange: boolean;
  isSelectingRange: boolean;
}

const Body: FC<IBody> = ({
  columns,
  rows,
  keys,
  totalRows,
  shouldPreventEditing,
  selectedRange,
  excludedRanges,
  selectedExcludedRanges,
  isSelectedCellInSelectedRange,
  selectedCell,
  isSelectingRange,
  totalColumns,
  toggleSelectTableRow,
  selectCell
}) => {
  const [lastHoveredCell, setLastHoveredCell] = useState<null | Cell>(null);

  const rowBaseClass = shouldPreventEditing ? 'ordering_form__table__prevent_editing_cell' : '';

  const cellClasses = useCallback(
    ({ row, column }: Cell) => {
      const prefix = 'ordering_form__table__';

      if ((column as number) > (totalColumns as number)) return [`${prefix}disabled_cell`];

      const cssClasses = [];

      if (containedInRanges({ row, column }, excludedRanges))
        cssClasses.push(`${prefix}excluded_cell`);

      if (
        inHoveringRange({ row, column }, selectedCell, lastHoveredCell, isSelectingRange) ||
        (isSelectedCellInSelectedRange && containedInRange({ row, column }, selectedRange))
      )
        cssClasses.push(`${prefix}selected_range_cell`);

      if (
        !isSelectedCellInSelectedRange &&
        containedInRanges({ row, column }, selectedExcludedRanges)
      )
        cssClasses.push(`${prefix}selected_excluded_cell`);

      if (shouldPreventEditing) cssClasses.push(`${prefix}prevent_editing_cell`);

      // All selectable table cells hover interaction becomes obvious with this class after
      // starting a range selection to show how to finish it
      if (
        (column as number) <= totalColumns &&
        (row as number) <= totalRows &&
        selectedRange.start &&
        !selectedRange.end
      )
        cssClasses.push(`${prefix}selectable`);

      return cssClasses;
    },
    [
      excludedRanges,
      isSelectedCellInSelectedRange,
      isSelectingRange,
      lastHoveredCell,
      selectedCell,
      selectedExcludedRanges,
      selectedRange,
      shouldPreventEditing,
      totalColumns,
      totalRows
    ]
  );

  const handleHover = useCallback(
    (cell: Cell) => () => {
      if (!isSelectingRange) return;
      if ((cell.row as number) > totalRows) return;
      if ((cell.column as number) > totalColumns) return;
      if (lastHoveredCell && isSameCell(lastHoveredCell, cell)) return;

      setLastHoveredCell(cell);
    },
    [isSelectingRange, lastHoveredCell, totalColumns, totalRows]
  );

  const handleOnClickToogle = (row: string | number) => () =>
    shouldPreventEditing ? null : toggleSelectTableRow(row);

  const handleOnClickSelect = (cell: Cell) => () => shouldPreventEditing ? null : selectCell(cell);

  useEffect(() => {
    if (selectedCell) {
      setLastHoveredCell(null);
    }
  }, [selectedCell]);

  return (
    <tbody>
      {rows.map((row, index) => {
        return (
          <tr
            key={`row-${row}`}
            id={`row-${row}`}
            className={cn(
              (row as number) > totalRows
                ? `${rowBaseClass} ordering_form__table__disabled_row`
                : rowBaseClass,
              index % 2 === 0 ? 'bg-white' : 'bg-tertiary-900'
            )}
          >
            <th id={`cell-0-${row}`} onClick={handleOnClickToogle(row)}>
              <div className="flex h-full flex-col items-center justify-center">{row}</div>
            </th>

            {columns.map((column) => {
              const columnChar = columnToChars(column);

              return (
                <td
                  id={`cell-${columnChar}-${row}`}
                  key={`cell-${columnChar}-${row}`}
                  className={cn(...cellClasses({ row, column }))}
                  onClick={handleOnClickSelect({ row, column })}
                  onMouseOver={handleHover({ row, column })}
                >
                  {keys[`${columnChar}${row}`]}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

export default connect(
  (state) => ({
    columns: getColumns(state),
    rows: getRows(state),
    keys: getKeys(state),
    totalRows: getTotalRows(state),
    totalColumns: getTotalColumns(state),
    selectedRange: getSelectedRange(state),
    excludedRanges: getExcludedRanges(state),
    selectedExcludedRanges: getSelectedExcludedRanges(state),
    isSelectedCellInSelectedRange: getIsSelectedCellInSelectedRange(state),
    shouldPreventEditing: getShouldPreventEditing(state),
    selectedCell: getSelectedCell(state),
    isSelectingRange: getIsSelectingRange(state)
  }),
  { toggleSelectTableRow, selectCell }
)(Body);
